import { pdkAxios } from '@/lib/pdk-axios.ts';
import type { ArchivedDeal } from '@/models/types.ts';
import {
  type InfiniteData,
  type QueryKey,
  useInfiniteQuery,
  useQuery,
  type UseQueryResult,
} from '@tanstack/react-query';
import { isNotFound } from '@tanstack/router-core';

export type DealFilter = {
  and?: FilterCondition[];
  or?: FilterCondition[];
};

export type FilterCondition = {
  and?: FilterCondition[];
  or?: FilterCondition[];
  searchText?: { containsIgnoreCase: string };
  hubPathIdentifier?: { equals: string };
};

type ArchivedDealsResponse = {
  items: ArchivedDeal[];
  nextCursor?: string | null;
};

type ArchivedDealsRequest = {
  cursor?: string;
  limit?: number;
  sort?: string;
  dealFilter?: DealFilter;
};

type HookOptions = {
  limit?: number;
  dealFilter?: DealFilter;
};

// TODO -- send config to BE to assert expected associations for a deal
// const ARCHIVED_DEAL_EXPECTED_ASSOCIATIONS: Array<keyof Omit<ArchivedDeal, keyof CoreDeal>> = [
//   'lender',
//   'hub',
//   'created_by',
//   'finance_manager',
//   'primary_sales_rep',
//   'secondary_sales_rep',
//   'payments',
//   'price_breakdown',
//   'customers',
//   'protection_plan',
//   'files',
//   'deal_task_assignments',
//   'audits',
//   'comments',
//   'emoji_reactions',
//   'confirmations',
// ];

const ARCHIVED_DEAL_QUERY_CONFIG = {
  staleTime: 30_000,
  gcTime: 10 * 60_000,
  retry: (failureCount: number, error: Error) =>
    !isNotFound(error) && failureCount < 1,
  retryDelay: 1000,
  refetchOnWindowFocus: false,
  refetchOnReconnect: true,
} as const;

async function fetchArchivedDeals(
  req: ArchivedDealsRequest,
): Promise<ArchivedDealsResponse> {
  const { data } = await pdkAxios.post<ArchivedDealsResponse>(
    '/archived-deals/query',
    req,
  );
  return data;
}

export async function fetchArchivedDealByDealId(
  dealId: string,
): Promise<ArchivedDeal> {
  const response = await pdkAxios.get<ArchivedDeal>(
    `/archived-deals/query/deal_id/${dealId}`,
  );
  return response.data;
}

export async function fetchArchivedDealsBulkByDealIds(
  dealIds: string[],
): Promise<ArchivedDeal[]> {
  const response = await pdkAxios.post<{ deals: ArchivedDeal[] }>(
    '/archived-deals/query/bulk/deal_ids',
    { deal_ids: dealIds },
  );
  return response.data.deals;
}

async function fetchArchivedDeal(dealShortId: string): Promise<ArchivedDeal> {
  const response = await pdkAxios.get<ArchivedDeal>(
    `/archived-deals/query/${dealShortId}`,
  );
  return response.data;
}

async function fetchArchivedDealByVehicleListingId(
  vehicleListingId: string,
): Promise<ArchivedDeal> {
  const response = await pdkAxios.get<ArchivedDeal>(
    `/archived-deals/query/vehicle_listing_id/${vehicleListingId}`,
  );
  return response.data;
}

export const useArchivedDeals = (options?: HookOptions) => {
  return useInfiniteQuery<
    ArchivedDealsResponse,
    Error,
    InfiniteData<ArchivedDealsResponse>,
    QueryKey,
    string | undefined
  >({
    queryKey: ['archivedDeals', options?.dealFilter, options?.limit],
    initialPageParam: undefined,
    queryFn: ({ pageParam }) =>
      fetchArchivedDeals({
        cursor: pageParam,
        limit: options?.limit ?? 50,
        dealFilter: options?.dealFilter,
      }),
    getNextPageParam: (lastPage) => lastPage.nextCursor ?? undefined,
    staleTime: 0,
    gcTime: 0,
  });
};

export function useArchivedDealQuery({
  shortId,
  enabled = false,
}: {
  shortId: string;
  enabled?: boolean;
}): UseQueryResult<ArchivedDeal, Error> {
  return useQuery({
    enabled,
    queryKey: ['archived-deal', 'shortId', shortId],
    queryFn: () => fetchArchivedDeal(shortId),
    ...ARCHIVED_DEAL_QUERY_CONFIG,
  });
}

export function useArchivedDealByDealIdQuery({
  dealId,
  enabled = false,
}: {
  dealId: string;
  enabled?: boolean;
}): UseQueryResult<ArchivedDeal, Error> {
  return useQuery({
    enabled,
    queryKey: ['archived-deal', 'dealId', dealId],
    queryFn: () => fetchArchivedDealByDealId(dealId),
    ...ARCHIVED_DEAL_QUERY_CONFIG,
  });
}

export function useArchivedDealByVehicleListingIdQuery({
  vehicleListingId,
  enabled = false,
}: {
  vehicleListingId: string;
  enabled?: boolean;
}): UseQueryResult<ArchivedDeal, Error> {
  return useQuery({
    enabled,
    queryKey: ['archived-deal', 'vehicleListingId', vehicleListingId],
    queryFn: () => fetchArchivedDealByVehicleListingId(vehicleListingId),
    ...ARCHIVED_DEAL_QUERY_CONFIG,
  });
}
